import { useContext } from "react"
import PropTypes from "prop-types"
import { capitalize, startsWith } from "lodash"
import { DESC } from "helpers/sorts"
import { ATTRIBUTION_METHODS } from "helpers/risk"
import { useAssetViewDashboardMetrics } from "helpers/metrics/hooks"
import { INSTRUMENTS } from "connected-components/dashboard/helpers"
import { useTemplateGatherers } from "connected-components/dashboard/hooks/use-template-gatherers"
import ContributorsDataIntegrator from "gatherers/contributors-data-integrator"
import ContributorsTable from "renderers/contributors-datagrid"
import { CLASSIFICATIONS_SHAPE } from "helpers/shapes/asset-view"
import { FACTOR_SENSITIVITY_CONTEXT } from "./context"
import { useAnalytics } from "connected-components/analytics"
import { useAssetViewHeaderHandlers } from "../../hooks/use-asset-view-header-handlers"

export default function AssetViewFactorSensitivity({ modelMetadata, context, instrumentsWithMetadata, highlightedRowIds, disableActiveTrading, instrumentColumns, classifications }) {
  const { selectedMetricDisplay, selectedCategory } = useContext(FACTOR_SENSITIVITY_CONTEXT)
  const {
    instrument: original,
    base: contextBase,
    groupBy,
    secondInstrument: rebalanced,
    comparisonInstrument: comparison,
    setTradedPosition,
    selectedTableLevels,
    setSelectedTableLevels,
    isExpanded,
    isEndVsStartDateComparison,
    view,
  } = useContext(context)

  const analytics = useAnalytics()
  const { handleSortChanged, handleTableNavigationChanged } = useAssetViewHeaderHandlers({ track: analytics.track })

  const attributionMethod = contextBase ? ATTRIBUTION_METHODS.MCTE : ATTRIBUTION_METHODS.MCAR

  const gatherers = useTemplateGatherers(context, view, {
    selectedMetricDisplay,
    attributionMethod,
    classifications,
  })

  const metrics = useAssetViewDashboardMetrics(
    [{ category: "factorSensitivity", selectedCategory, factorSensitivityType: view, selectedMetricDisplay }],
    original,
    rebalanced,
    comparison,
    { instrumentColumns, isActive: !!contextBase, modelMetadata, instrumentsWithMetadata, setTradedPosition, isExpanded, disableActiveTrading, isEndVsStartDateComparison },
  )

  const defaultSortSuffix = INSTRUMENTS[instrumentColumns[0]].suffix
  const downloadNameDescriptor = startsWith(view, "predicted") ? "Predicted Factor Sensitivity" : "Historical Factor Sensitivity"

  return <ContributorsDataIntegrator
    gatherers={gatherers}
    metrics={metrics}
    Renderer={ContributorsTable}
    rendererProps={{
        highlightedRowIds,
        groupByName: groupBy?.label,
        defaultSort: {
          path: `marketValue${capitalize(defaultSortSuffix)}`,
          direction: DESC,
        },
        downloadKey: `assets.${view}`,
        downloadName: `Assets - ${downloadNameDescriptor}`,
        tableNavigationLevels: selectedTableLevels,
        onTableNavigationChange: setSelectedTableLevels,
        // Analytics callbacks
        onSortChanged: handleSortChanged,
        onTableNavigationChanged: handleTableNavigationChanged,
        context,
    }}
  />
}

AssetViewFactorSensitivity.propTypes = {
  modelMetadata: PropTypes.object.isRequired,
  context: PropTypes.object.isRequired,
  instrumentsWithMetadata: PropTypes.object.isRequired,
  highlightedRowIds: PropTypes.arrayOf(PropTypes.string),
  disableActiveTrading: PropTypes.bool.isRequired,
  instrumentColumns: PropTypes.arrayOf(PropTypes.string).isRequired,
  classifications: CLASSIFICATIONS_SHAPE.isRequired,
}
